import React from 'react';
import { IconButton, CircularProgress, Box } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { usePlaceFavorite } from '../hooks/usePlaceFavorite';
import { Place } from '@/types/place';
import theme from './Theme';

interface FavoritesComponentProps {
  placeId: string;
  isAuthenticated: boolean;
  userId: string | null;
  authToken: string | null;
  favorites?: Place[]; // Optional cached favorites array
  refetchPlaceData?: () => void;
  onUpdateFavorites?: () => void;
}

/**
 * Favorites component with optimistic updates.
 * Uses TanStack Query via usePlaceFavorite hook.
 */
export const FavoritesComponent: React.FC<FavoritesComponentProps> = ({
  placeId,
  isAuthenticated,
  userId,
  authToken,
  favorites,
  refetchPlaceData,
  onUpdateFavorites,
}) => {
  // Use cached favorites if provided, otherwise hook will handle it
  const { isFavorite, isLoading, toggleFavorite, isToggling } = usePlaceFavorite(
    userId,
    placeId,
    favorites
  );

  // Early return if not authenticated - component should not be mounted in this case
  // but this is a safety check
  if (!isAuthenticated || !userId || userId.trim() === '') {
    return null;
  }

  const handleToggle = () => {
    toggleFavorite();
    if (onUpdateFavorites) {
      onUpdateFavorites();
    }
    if (refetchPlaceData) {
      setTimeout(() => refetchPlaceData(), 500); // Small delay for mutation to complete
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 48 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <IconButton
      onClick={handleToggle}
      disabled={isToggling}
      sx={{ minWidth: 48, minHeight: 48 }} // Touch target size
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
    >
      {isFavorite ? (
        <FavoriteIcon sx={{ color: theme.palette.accent.main, fontSize: '1.75rem' }} />
      ) : (
        <FavoriteBorderIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.75rem' }} />
      )}
    </IconButton>
  );
};

export default FavoritesComponent;
